/**
 * What a violation says, in our words.
 *
 * The service sends `{ rule, anchor, params }` and no prose — deliberately: it
 * carries no step names and no exit labels, because those are things the canvas
 * already has names for and it would otherwise be writing copy that disagreed
 * with the blocks around it. So the sentence is built here, from the rule and
 * the facts behind it.
 *
 * Every rule gets a sentence. An unknown one gets its own name humanised rather
 * than nothing: the list of rules is append-only upstream, and a reader meeting
 * a rule this build has not seen is better served by a bare name than by a
 * finding that blocks publishing and says nothing.
 */

import { AOPViolation, AOPViolationParams, AOPViolationRule } from '../../../types/AOPTypes';
import { exitLabel, formatSeconds, humaniseExitName } from './AOPPlanUtils';

const listOf = (names: string[] | undefined, asExit: boolean): string => {
	const items = names ? names : [];
	const words = items.map((name) => (asExit ? `“${exitLabel(name)}”` : `{{ ${name} }}`));

	if (words.length <= 1) {
		return words.join('');
	}

	return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
};

const plural = (names: string[] | undefined): boolean => !!names && names.length > 1;

/**
 * One sentence per rule.
 *
 * Written to be read on a block and in the publish-blocked list, so each says
 * what is wrong rather than naming the rule: "Nothing reaches this step", not
 * "unreachable_step".
 */
const SENTENCES: { [rule in AOPViolationRule]: (params: AOPViolationParams) => string } = {
	unsupported_step: (p) =>
		p.request
			? `Radar cannot do this: “${p.request}”. Choose one of the alternatives.`
			: 'Radar cannot do what this step asks. Choose one of the alternatives.',

	missing_exit: (p) =>
		`${plural(p.exits) ? 'Needs these branches' : 'Needs a branch for'} ${listOf(p.exits, true)}.`,

	no_answer_exit: () => 'Asks a question with no answer to take, so nothing can come of it.',

	extra_exit: (p) =>
		`${listOf(p.exits, true)} ${
			plural(p.exits) ? 'are branches' : 'is a branch'
		} this step never takes.`,

	duplicate_exit: (p) => `${listOf(p.exits, true)} is named twice. Each branch needs its own name.`,

	dangling_exit: (p) =>
		`${listOf(p.exits, true)} ${
			plural(p.exits) ? 'point' : 'points'
		} at a step that is not in the plan.`,

	unreachable_step: () => 'Nothing reaches this step, so Radar never runs it.',

	dead_end: () => 'This step never reaches an ending, so a run that arrives here cannot finish.',

	never_finishes: () => 'The plan has no ending, so a run would never stop.',

	false_success: (p) =>
		`Its ${
			p.exit ? `“${exitLabel(p.exit)}”` : 'failing'
		} branch ends in success, though it is the branch taken when things did not work.`,

	duplicate_step: () => 'Another step already has this name.',

	unknown_placeholder: (p) =>
		`Nothing fills in ${listOf(p.placeholders, false)}, so ${
			plural(p.placeholders) ? 'they stay' : 'it stays'
		} in the note as written.`,

	past_time_limit: (p) =>
		p.waited_seconds && p.max_run_seconds
			? `Waits ${formatSeconds(p.waited_seconds)} in all, past this procedure's ${formatSeconds(
					p.max_run_seconds
			  )} limit.`
			: 'Waits longer in all than this procedure is allowed to run.',
};

export const violationMessage = (violation: AOPViolation): string => {
	const sentence = SENTENCES[violation.rule];
	return sentence
		? sentence(violation.params ? violation.params : {})
		: humaniseExitName(violation.rule);
};
